import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { articleGenerationService } from '../../services/articleGenerationService';
import { ArticleGenerationRow, LlmLogRow, TraceEntry } from '../../types/articleGeneration';
import MaterialIcon from '../shared/MaterialIcon';

interface Props {
  generationId: string;
  // Poll while the run is still generating so new calls appear live.
  live?: boolean;
  // The run row: its status, error and persisted debug trace are shown as
  // diagnostics, so a run that died can be understood even when no model call
  // was ever logged.
  run?: ArticleGenerationRow | null;
}

const STATUS_ICON: Record<string, { name: string; className: string }> = {
  pending: { name: 'progress_activity', className: 'text-blue-500 animate-spin' },
  ok: { name: 'check_circle', className: 'text-green-600' },
  error: { name: 'error', className: 'text-red-600' },
};

const copy = (text: string) => navigator.clipboard.writeText(text).catch(() => undefined);

const compact = (detail?: Record<string, unknown>) =>
  detail
    ? Object.entries(detail)
        .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
        .join('  ')
    : '';

const TRACE_TONE: Record<string, string> = {
  pipeline_error: 'text-red-700',
  llm_error: 'text-red-700',
  llm_log_insert_failed: 'text-red-700',
  llm_log_update_failed: 'text-red-700',
  beforeunload: 'text-amber-700 font-semibold',
  unhandledrejection: 'text-red-700',
  pipeline_start: 'font-semibold',
  pipeline_end: 'font-semibold',
};

const Diagnostics: React.FC<{ run: ArticleGenerationRow }> = ({ run }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const trace: TraceEntry[] = run.progress?.trace ?? [];
  const shutdown = trace.find((e) => e.event === 'beforeunload');
  const insertFail = trace.find((e) => e.event === 'llm_log_insert_failed');
  const elapsedS = Math.max(
    0,
    Math.round((new Date(run.updated_at).getTime() - new Date(run.created_at).getTime()) / 1000)
  );

  return (
    <div className="border rounded-lg bg-gray-50 mb-4" data-testid="log-diagnostics">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm"
      >
        <MaterialIcon name={open ? 'expand_more' : 'chevron_right'} className="text-base" />
        <MaterialIcon name="troubleshoot" className="text-base text-gray-500" />
        <span className="font-medium">{t('article.log_diagnostics')}</span>
        <span className="text-xs text-gray-500">
          {t('article.log_run_status')}: <span className="font-mono">{run.status}</span>
          {' · '}
          {t('article.log_elapsed', { seconds: elapsedS })}
          {' · '}
          {t('article.log_trace_count', { count: trace.length })}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 text-sm">
          {run.error_message && (
            <div className="border border-red-200 rounded p-2 bg-red-50 text-red-700 text-xs">
              <span className="font-semibold">{t('article.log_error_message')}:</span>{' '}
              {run.error_message}
            </div>
          )}
          {shutdown && (
            <div className="border border-amber-200 rounded p-2 bg-amber-50 text-amber-800 text-xs">
              {t('article.log_hint_shutdown', {
                seconds: (shutdown.detail?.elapsedS as number | undefined) ?? Math.round(shutdown.ms / 1000),
              })}
            </div>
          )}
          {insertFail && (
            <div className="border border-amber-200 rounded p-2 bg-amber-50 text-amber-800 text-xs">
              <span className="font-semibold">{t('article.log_hint_insert_failed')}:</span>{' '}
              {String(insertFail.detail?.error ?? '')}
            </div>
          )}

          {trace.length === 0 ? (
            <div className="text-xs text-gray-500">{t('article.log_no_trace')}</div>
          ) : (
            <ol className="font-mono text-[11px] leading-5 border rounded bg-white p-2 overflow-x-auto">
              {trace.map((e, i) => (
                <li key={i} className={`whitespace-nowrap ${TRACE_TONE[e.event] ?? 'text-gray-700'}`}>
                  <span className="text-gray-400">+{(e.ms / 1000).toFixed(1)}s</span>{' '}
                  <span>{e.event}</span>
                  {e.detail && <span className="text-gray-500">  {compact(e.detail)}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

const LogEntry: React.FC<{ log: LlmLogRow }> = ({ log }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const messages = log.request.messages ?? [];
  const tokens = log.usage?.total_tokens;

  return (
    <li className="border rounded-lg bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm"
      >
        <MaterialIcon name={open ? 'expand_more' : 'chevron_right'} className="text-base" />
        <MaterialIcon
          name={(STATUS_ICON[log.status] ?? STATUS_ICON.ok).name}
          className={`text-sm ${(STATUS_ICON[log.status] ?? STATUS_ICON.ok).className}`}
        />
        <span className="font-medium">{log.stage}</span>
        <span className="font-mono text-xs text-gray-400 truncate">{log.model}</span>
        <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
          {log.sent_at ? new Date(log.sent_at).toLocaleTimeString() : '—'}
          {log.duration_ms != null && ` · ${(log.duration_ms / 1000).toFixed(1)}s`}
          {tokens != null && ` · ${tokens} tok`}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 text-sm">
          <div className="text-xs text-gray-500">
            {t('article.log_meta', {
              status: log.status,
              sent: log.sent_at ? new Date(log.sent_at).toLocaleString() : '—',
              duration: log.duration_ms != null ? `${log.duration_ms} ms` : '—',
            })}
            {log.usage && (
              <>
                {' · '}
                {t('article.log_tokens', {
                  prompt: log.usage.prompt_tokens ?? '?',
                  completion: log.usage.completion_tokens ?? '?',
                })}
              </>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600">
                {t('article.log_prompt')}
              </span>
              <button
                type="button"
                onClick={() => copy(messages.map((m) => `[${m.role}]\n${m.content}`).join('\n\n'))}
                className="text-xs text-blue-600 hover:underline"
              >
                {t('article.copy_markdown')}
              </button>
            </div>
            {messages.map((m, i) => (
              <div key={i} className="mb-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-400">{m.role}</div>
                <pre className="whitespace-pre-wrap break-words text-xs border rounded p-2 bg-gray-50 overflow-x-auto">
                  {m.content}
                </pre>
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600">
                {t('article.log_response')}
              </span>
              {log.response && (
                <button
                  type="button"
                  onClick={() => copy(log.response ?? '')}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {t('article.copy_markdown')}
                </button>
              )}
            </div>
            {log.status === 'error' ? (
              <pre className="whitespace-pre-wrap break-words text-xs border border-red-200 rounded p-2 bg-red-50 text-red-700 overflow-x-auto">
                {log.error_message || t('article.log_no_response')}
              </pre>
            ) : log.status === 'pending' ? (
              <div className="text-xs text-blue-600 inline-flex items-center gap-1">
                <MaterialIcon name="progress_activity" className="text-sm animate-spin" />
                {t('article.log_awaiting')}
              </div>
            ) : (
              <pre className="whitespace-pre-wrap break-words text-xs border rounded p-2 bg-white overflow-x-auto">
                {log.response || t('article.log_no_response')}
              </pre>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

const errorText = (e: unknown) =>
  e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e);

const LogPanel: React.FC<Props> = ({ generationId, live = false, run = null }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<LlmLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  // A failing query must never look like "no calls": show the real error.
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchLogs = () =>
    articleGenerationService
      .listLlmLogs(generationId)
      .then((rows) => {
        setLogs(rows);
        setLoadError(null);
      })
      .catch((e) => {
        console.error('LogPanel: failed to load the model-call log', e);
        setLogs([]);
        setLoadError(errorText(e));
      });

  const reload = () => {
    setLoading(true);
    fetchLogs().finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationId]);

  // Poll while the run is generating or any call is still pending, so calls
  // and their responses appear the moment they happen.
  const hasPending = logs.some((l) => l.status === 'pending');
  useEffect(() => {
    if (!live && !hasPending) return;
    const iv = setInterval(fetchLogs, 2500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationId, live, hasPending]);

  return (
    <div>
      {run && <Diagnostics run={run} />}

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">
          {t('article.log_title')}{' '}
          <span className="text-sm font-normal text-gray-500">({logs.length})</span>
        </h3>
        <button
          type="button"
          onClick={reload}
          className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
        >
          <MaterialIcon name="refresh" className="text-sm" />
          {t('article.log_refresh')}
        </button>
      </div>

      {loadError ? (
        <div className="border border-red-200 rounded p-3 bg-red-50 text-red-700 text-sm">
          <span className="font-semibold">{t('article.log_query_failed')}:</span> {loadError}
        </div>
      ) : loading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : logs.length === 0 ? (
        <div className="text-sm text-gray-500">{t('article.log_empty')}</div>
      ) : (
        <ul className="space-y-2">
          {logs.map((log) => (
            <LogEntry key={log.id} log={log} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default LogPanel;
